import axios from 'axios';
import { getAuth } from 'firebase/auth';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

export interface BrandColor {
    id: number;
    name: string;
    hex?: string;
    brandId: number;
    active: boolean;
}

export interface BrandSize {
    id: number;
    name: string;
    order: number;
    brandId: number;
    active: boolean;
}

export interface BrandAttribute {
    id: number;
    name: string;
    value: string;
    brandId?: number;
    active: boolean;
}

export interface Brand {
    id: number;
    name: string;
    code: string;
    active: boolean;
    colors?: BrandColor[];
    sizes?: BrandSize[];
    attributes?: BrandAttribute[];
    _count?: {
        colors: number;
        sizes: number;
    };
}

const getHeaders = async () => {
    const auth = getAuth();
    const token = await auth.currentUser?.getIdToken();
    return {
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
        }
    };
};

export const brandService = {
    getBrands: async (active?: boolean, search?: string) => {
        const headers = await getHeaders();
        let query = '?';
        if (active !== undefined) query += `active=${active}&`;
        if (search) query += `search=${encodeURIComponent(search)}`;

        const response = await axios.get<{ success: boolean; data: Brand[] }>(`${API_URL}/brands${query}`, headers);
        return response.data;
    },

    getBrandById: async (id: number) => {
        const headers = await getHeaders();
        const response = await axios.get<{ success: boolean; data: Brand }>(`${API_URL}/brands/${id}`, headers);
        return response.data;
    },

    createBrand: async (data: { name: string; code?: string }) => {
        const headers = await getHeaders();
        const response = await axios.post<{ success: boolean; data: Brand }>(`${API_URL}/brands`, data, headers);
        return response.data;
    },

    updateBrand: async (id: number, data: { name?: string; code?: string; active?: boolean }) => {
        const headers = await getHeaders();
        const response = await axios.put<{ success: boolean; data: Brand }>(`${API_URL}/brands/${id}`, data, headers);
        return response.data;
    },

    deleteBrand: async (id: number) => {
        const headers = await getHeaders();
        const response = await axios.delete<{ success: boolean; message: string }>(`${API_URL}/brands/${id}`, headers);
        return response.data;
    },

    // Sub-resources
    addColor: async (brandId: number, data: { name: string; hex?: string }) => {
        const headers = await getHeaders();
        const response = await axios.post<{ success: boolean; data: BrandColor }>(`${API_URL}/brands/${brandId}/colors`, data, headers);
        return response.data;
    },

    deleteColor: async (_brandId: number, colorId: number) => {
        const headers = await getHeaders(); // brandId needed for route? route is /brands/colors/:id based on backend?
        // Backend route: router.delete('/colors/:colorId', ...)
        // Wait, my backend implementation for delete color/size was:
        // router.delete('/colors/:colorId', requireAdmin, deleteBrandColor);
        // So distinct from brandId in URL.
        const response = await axios.delete<{ success: boolean; message: string }>(`${API_URL}/brands/colors/${colorId}`, headers);
        return response.data;
    },

    addSize: async (brandId: number, data: { name: string; order?: number }) => {
        const headers = await getHeaders();
        const response = await axios.post<{ success: boolean; data: BrandSize }>(`${API_URL}/brands/${brandId}/sizes`, data, headers);
        return response.data;
    },

    deleteSize: async (_brandId: number, sizeId: number) => {
        const headers = await getHeaders();
        const response = await axios.delete<{ success: boolean; message: string }>(`${API_URL}/brands/sizes/${sizeId}`, headers);
        return response.data;
    }
};
